import { Outlet } from "react-router-dom";
import { useUserConfiguration, Roles, UserConfigurationType } from "../hooks/useUserConfiguration";
import { Forbidden } from "../pages/errors/Forbidden";

export const ValidatedRoutes = ({
  validate,
}: {
  validate: (userConfiguration: UserConfigurationType) => boolean;
}) => {
  /*
   * This component is used to guard routes based on user configuration.
   * It takes a "validate" function that receives the user configuration and returns a boolean.
   * If the validate function returns false, the user is redirected to the Forbidden page.
   * If the built-in validations of the related PermissionedRoutes component are not enough, you can use this component
   * to implement custom validation (e.g. an inclusive OR between permissions).
   */
  const userConfiguration = useUserConfiguration();

  if (!validate(userConfiguration)) {
    return <Forbidden />;
  }

  return <Outlet />;
};

export const PermissionedRoutes = ({
  requiredPermissions,
  requiredFeatures,
  requiredRoles,
}: {
  requiredPermissions?: string[];
  requiredFeatures?: string[];
  requiredRoles?: (keyof Roles)[];
}) => {
  /*
   * This component is used to guard routes based an inclusive AND between permissions, features, and roles.
   * It takes three optional props: requiredPermissions, requiredFeatures, and requiredRoles.
   * For any defined prop, the user must have all the permissions, features, or roles specified in the array.
   * Essentially, this component is a wrapper around the ValidatedRoutes component that implements
   * the most common validations.
   */
  const checkPermissions = (userConfiguration: UserConfigurationType) => {
    if (!requiredPermissions) return true;
    return requiredPermissions.every((permission) =>
      userConfiguration.permissions.includes(permission)
    );
  };

  const checkFeatures = (userConfiguration: UserConfigurationType) => {
    if (!requiredFeatures) return true;
    return requiredFeatures.every((feature) => userConfiguration.features.includes(feature));
  };

  const checkRoles = (userConfiguration: UserConfigurationType) => {
    if (!requiredRoles) return true;
    return requiredRoles.every((role) => userConfiguration.roles[role]);
  };

  return (
    <ValidatedRoutes
      validate={(userConfiguration) =>
        checkPermissions(userConfiguration) &&
        checkFeatures(userConfiguration) &&
        checkRoles(userConfiguration)
      }
    />
  );
};

export const UniversalMigrationFeatureRoutes = ({ features }: { features: string[] }) => {
  const userConfiguration = useUserConfiguration();

  if (!features.every((feature) => userConfiguration.features.includes(feature))) {
    window.location.reload(); // force an SSR reload to trigger the old page.
    return null;
  }

  return <Outlet />;
};
